import { useState } from "react"
import StarBorderIcon from "@mui/icons-material/StarBorder"
import StarIcon from "@mui/icons-material/Star"
import NorthIcon from "@mui/icons-material/North"
import { formatPurchaseDate, purchaseScheduleLabel } from "../../../util/purchaseDate"

const NewProductForm = (props) => {
  const setProducts = props.setProducts || (() => {})
  const categories = props.categories || []

  const [inputValue, setInputValue] = useState({
    productName: "",
    productPrice: "",
    productCompPrice: "",
    productDescription: "",
    productImage: "",
    productMinQty: "",
    productPopularity: "",
    productWeight: "",
    productLength: "",
    productWidth: "",
    productHeight: "",
    merchantName: "",
    merchantEmail: "",
    merchantWebsite: "",
    merchantPhone: "",
  })

  const [featured, setFeatured] = useState(false)
  const [mostFilled, setMostFilled] = useState(false)
  const [category, setCategory] = useState("")
  const [subCategory, setSubcategory] = useState("")

  const {
    productName,
    productPrice,
    productCompPrice,
    productDescription,
    productImage,
    productMinQty,
    productPopularity,
    productWeight,
    productLength,
    productWidth,
    productHeight,
    merchantName,
    merchantEmail,
    merchantWebsite,
    merchantPhone,
  } = inputValue

  /* ================================
     INPUT CHANGE
  ================================= */
  const handleOnChange = (e) => {
    const { name, value } = e.target
    setInputValue((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  /* ================================
     SUBMIT (FIXED PROCESSES ONLY)
  ================================= */
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!productName.trim()) return alert("Product name is required")

    if (!productImage.trim()) return alert("Product image is required")

    if (!productPrice || isNaN(productPrice))
      return alert("Valid price is required")

    if (!category?.name) return alert("Please select a category")

    const productDetails = {
      name: productName.trim(),
      price: Number(productPrice),
      // `category` holds the whole category document; the API wants its name
      category: category.name,
      subCategory,
      description: productDescription.trim(),
      image: productImage.trim(),
      featured,
      mostFilled,

      ...(productCompPrice !== "" && {
        comp_price: Number(productCompPrice),
      }),

      ...(productMinQty !== "" && {
        min_qty: Number(productMinQty),
      }),

      ...(productPopularity !== "" && {
        popularity: Number(productPopularity),
      }),

      ...(productWeight !== "" && {
        weight: Number(productWeight),
      }),

      ...(productLength !== "" && {
        length: Number(productLength),
      }),

      ...(productWidth !== "" && {
        width: Number(productWidth),
      }),

      ...(productHeight !== "" && {
        height: Number(productHeight),
      }),

      merchant_info: {
        name: merchantName.trim(),
        email: merchantEmail.trim(),
        website: merchantWebsite.trim(),
        phone_number: merchantPhone.trim(),
      },
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/products`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productDetails),
      })

      if (!res.ok) {
        const error = await res.text()
        console.error(error)
        return alert("Failed to create product")
      }

      const data = await res.json()
      setProducts((prev) => [...(prev || []), data])

      clearInputValue()
      setFeatured(false)
      setMostFilled(false)

      alert("Product Successfully submitted!")
    } catch (err) {
      console.error("Submit failed:", err)
      alert("Server error")
    }
  }

  const toggleFeatured = () => setFeatured((prev) => !prev)
  const toggleMostFilled = () => setMostFilled((prev) => !prev)

  const clearInputValue = () => {
    setInputValue({
      productName: "",
      productPrice: "",
      productCompPrice: "",
      productDescription: "",
      productImage: "",
      productMinQty: "",
      productPopularity: "",
      productWeight: "",
      productLength: "",
      productWidth: "",
      productHeight: "",
      merchantName: "",
      merchantEmail: "",
      merchantWebsite: "",
      merchantPhone: "",
    })
  }

  return (
    <div className="NewProductForm w-100 d-flex flex-column align-items-center">
      <div className="col-12 text-start ms-3">
        <div className="col-10 py-2 mb-4 mx-auto">
          <div className="row justify-content-end px-0">
            <div className="col-3">
              <div className="col-12 d-flex justify-content-end align-items-center mt-3">
                <button
                  className="btn btn-danger"
                  onClick={() => clearInputValue()}
                >
                  Clear form
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Name</label>

              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <input
                    type="text"
                    className="form-control"
                    name="productName"
                    value={productName}
                    onChange={handleOnChange}
                  />
                </div>

                {/* Featured toggle */}
                <button
                  type="button"
                  className="btn border-0 bg-transparent ms-2"
                  onClick={toggleFeatured}
                  style={{ fontSize: "28px", cursor: "pointer" }}
                  title={featured ? "Unmark Featured" : "Mark as Featured"}
                >
                  {featured ? (
                    <StarIcon style={{ fontSize: 28, color: "#ffb300" }} />
                  ) : (
                    <StarBorderIcon style={{ fontSize: 28 }} />
                  )}
                </button>

                {/* Most-filled toggle */}
                <button
                  type="button"
                  className="btn border-0 bg-transparent ms-2"
                  onClick={toggleMostFilled}
                  style={{ fontSize: 28, cursor: "pointer" }}
                  title={
                    mostFilled ? "Unmark Most Filled" : "Mark as Most Filled"
                  }
                >
                  {mostFilled ? (
                    <NorthIcon style={{ fontSize: 28, color: "#ffb300" }} />
                  ) : (
                    <NorthIcon style={{ fontSize: 28 }} />
                  )}
                </button>
              </div>
            </div>

            <div className="mb-3 row gx-2">
              {/* Price */}
              <div className="col-3 text-center">
                <label className="form-label">Price</label>
                <div className="input-group">
                  <span className="input-group-text fs-5">₱</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="form-control fs-5"
                    name="productPrice"
                    value={productPrice}
                    onChange={handleOnChange}
                  />
                </div>
              </div>

              {/* Compared Price */}
              <div className="col-3 text-center">
                <label className="form-label">Compared Price</label>
                <div className="input-group">
                  <span className="input-group-text fs-5">₱</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="form-control fs-5"
                    name="productCompPrice"
                    value={productCompPrice}
                    onChange={handleOnChange}
                  />
                </div>
              </div>

              {/* Category */}
              <div className="col-3 text-center">
                <label className="form-label">Category</label>

                <select
                  className="form-select form-select-lg"
                  value={category?._id || ""}
                  onChange={(e) => {
                    const selected = categories.find(
                      (c) => c._id === e.target.value,
                    )
                    console.log("SELECTED:", selected)
                    setCategory(selected)
                    setSubcategory("")
                  }}
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Subcategory */}
              <div className="col-3 text-center">
                <label className="form-label">Subcategory</label>

                <select
                  className="form-select form-select-lg"
                  value={subCategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  disabled={!category}
                >
                  <option value="">Select Subcategory</option>

                  {category?.subCategories?.map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="productDescription" className="form-label">
                Description
              </label>
              <textarea
                type="text"
                rows="5"
                className="form-control"
                name="productDescription"
                value={productDescription}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="productPopularity" className="form-label">
                Popularity (0-100)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                className="form-control"
                name="productPopularity"
                value={productPopularity}
                onChange={handleOnChange}
              />
              <div className="form-text">
                How commonly restaurants buy this. Higher numbers show first on the Products page.
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="productImage" className="form-label">
                Image URL
              </label>
              <input
                type="text"
                className="form-control"
                name="productImage"
                value={productImage}
                onChange={handleOnChange}
              />
            </div>

            <div className="mb-3 row">
              <div className="col-4 text-center">
                <label htmlFor="productMinQty" className="form-label">
                  Min Qty for Order
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="productMinQty"
                    value={productMinQty}
                    onChange={handleOnChange}
                  />
                  <span className="input-group-text fs-6">
                    {Number(productMinQty) > 1 ? "cases/kilos" : "case/kilo"}
                  </span>
                </div>
              </div>
              <div className="col-4 text-center">
                <label htmlFor="productWeight" className="form-label">
                  Weight
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control fs-5"
                    name="productWeight"
                    value={productWeight}
                    onChange={handleOnChange}
                  />
                  <span className="input-group-text fs-6">
                    {Number(productWeight) > 1 ? "kgs" : "kg"}
                  </span>
                </div>
              </div>
              <div className="col-4 text-center">
                <label className="form-label">Purchase Date</label>
                <div className="form-control bg-light">
                  {purchaseScheduleLabel(category?.name)}
                  <div className="small text-muted">
                    Next: {formatPurchaseDate(category?.name)}
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <div className="row gx-2 mt-1">
                <div className="col-4 text-center">
                  <label htmlFor="productLength" className="form-label">
                    Length
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control fs-5"
                      name="productLength"
                      value={productLength}
                      onChange={handleOnChange}
                    />
                    <span className="input-group-text fs-6">(cm)</span>
                  </div>
                </div>
                <div className="col-4 text-center">
                  <label htmlFor="productWidth" className="form-label">
                    Width
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control fs-5"
                      name="productWidth"
                      value={productWidth}
                      onChange={handleOnChange}
                    />
                    <span className="input-group-text fs-6">(cm)</span>
                  </div>
                </div>
                <div className="col-4 text-center">
                  <label htmlFor="productHeight" className="form-label">
                    Height
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control fs-5"
                      name="productHeight"
                      value={productHeight}
                      onChange={handleOnChange}
                    />
                    <span className="input-group-text fs-6">(cm)</span>
                  </div>
                </div>
              </div>
            </div>
            <h1 className="display-5 mt-5 mb-3">Merchant Info</h1>
            <div className="mb-3">
              <label htmlFor="merchantName" className="form-label">
                Merchant Name
              </label>
              <input
                type="text"
                className="form-control"
                name="merchantName"
                value={merchantName}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="merchantEmail" className="form-label">
                Merchant Email
              </label>
              <input
                type="text"
                className="form-control"
                name="merchantEmail"
                value={merchantEmail}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="merchantWebsite" className="form-label">
                Merchant Website
              </label>
              <input
                type="text"
                className="form-control"
                name="merchantWebsite"
                value={merchantWebsite}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="merchantPhone" className="form-label">
                Merchant Phone
              </label>
              <input
                type="text"
                className="form-control"
                name="merchantPhone"
                value={merchantPhone}
                onChange={handleOnChange}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              data-bs-dismiss="modal"
              data-bs-target="#newProductModal"
              aria-label="Close"
            >
              Submit Product
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
export default NewProductForm
